import { useCallback, useEffect, useMemo, useState } from "react";
import { getLolPregame } from "../../../services/lolPregameService";

const american = (p) => {
  if (!Number.isFinite(p) || p <= 0 || p >= 1) return "—";
  const odds = p >= 0.5 ? Math.round((-100 * p) / (1 - p)) : Math.round((100 * (1 - p)) / p);
  return odds > 0 ? `+${odds}` : `${odds}`;
};

const pct = (p) => (Number.isFinite(p) ? `${(p * 100).toFixed(1)}%` : "");

const roiOf = (model, market) =>
  Number.isFinite(model) && Number.isFinite(market) && market > 0 ? model / market - 1 : null;

function rowsFor(match) {
  const model = match.model || {};
  const market = match.market || {};
  const single = match.best_of === 1;
  const rows = [];

  if (!single && model.map1) {
    rows.push({
      label: "Map 1",
      url: market.map1 && market.map1.url,
      a: [model.map1.team_a, market.map1 && market.map1.team_a],
      b: [model.map1.team_b, market.map1 && market.map1.team_b],
    });
  }

  if (model.series) {
    rows.push({
      label: single ? "Match" : "Series",
      url: market.series && market.series.url,
      a: [model.series.team_a, market.series && market.series.team_a],
      b: [model.series.team_b, market.series && market.series.team_b],
    });
  }

  const lines = single
    ? []
    : Object.keys((model.handicap && model.handicap.team_a) || {})
        .filter(
          (line) =>
            Math.abs(Number(line)) === 1.5 ||
            (market.handicap && line in (market.handicap.team_a || {}))
        )
        .sort((x, y) => Number(x) - Number(y));

  lines.forEach((line) => {
    const oppositeNumber = -Number(line);
    const opposite = `${oppositeNumber >= 0 ? "+" : ""}${oppositeNumber.toFixed(1)}`;
    rows.push({
      label: "Handicap",
      line: `${line} / ${opposite}`,
      url: market.handicap && market.handicap.url,
      a: [
        model.handicap.team_a[line],
        market.handicap && market.handicap.team_a && market.handicap.team_a[line],
      ],
      b: [
        model.handicap.team_b[opposite],
        market.handicap && market.handicap.team_b && market.handicap.team_b[opposite],
      ],
    });
  });

  return rows.map((row) => ({ ...row, roiA: roiOf(row.a[0], row.a[1]), roiB: roiOf(row.b[0], row.b[1]) }));
}

function Price({ value }) {
  if (!Number.isFinite(value)) return <span className="lol-na">—</span>;
  return (
    <span className="lol-price">
      <span className="lol-amer">{american(value)}</span>
      <span className="lol-pct">{pct(value)}</span>
    </span>
  );
}

function Roi({ value }) {
  if (value === null || value <= 0) return null;
  return <span className="lol-roi">+{(value * 100).toFixed(1)}%</span>;
}

function Card({ match }) {
  const rows = rowsFor(match);
  const market = match.market || {};
  const series = (match.model && match.model.series) || {};
  const priced = Boolean(market.series || market.map1);

  return (
    <article className="lol-card">
      <div className="lol-when">
        <span>{match.match_date}</span>
        {match.stage && <span>{match.stage}</span>}
        <span className="lol-pill">Bo{match.best_of}</span>
        {market.best_of && market.best_of !== match.best_of && (
          <span className="lol-pill lol-pill-warn">Polymarket lists Bo{market.best_of}</span>
        )}
        {match.backfill && <span className="lol-pill lol-pill-warn">as-of backfill</span>}
        {!priced && <span>no Polymarket market found</span>}
      </div>
      <div className="lol-matchup">
        <div className="lol-team">
          <span className="lol-name" title={match.team_a}>{match.team_a}</span>
          <span className={`lol-prob ${series.team_a >= 0.5 ? "is-fav" : ""}`}>{pct(series.team_a)}</span>
        </div>
        <span className="lol-vs">vs</span>
        <div className="lol-team lol-team-b">
          <span className="lol-name" title={match.team_b}>{match.team_b}</span>
          <span className={`lol-prob ${series.team_b >= 0.5 ? "is-fav" : ""}`}>{pct(series.team_b)}</span>
        </div>
      </div>
      <div className="lol-grid">
        <div className="lol-h lol-mk" />
        <div className="lol-h lol-teamhead">{match.team_a}</div>
        <div className="lol-h lol-teamhead">{match.team_b}</div>
        <div className="lol-sub lol-mk">Market</div>
        <div className="lol-subcols">
          <span>Model</span><span>Poly</span><span>ROI</span>
        </div>
        <div className="lol-subcols">
          <span>Model</span><span>Poly</span><span>ROI</span>
        </div>
        {rows.map((row) => (
          <div className="lol-row" key={`${row.label}-${row.line || ""}`}>
            <div className="lol-mk">
              {row.label}
              {row.line && <span className="lol-line">{row.line}</span>}
              {row.url && (
                <a href={row.url} target="_blank" rel="noopener noreferrer" title={`Open ${row.label} on Polymarket`}>↗</a>
              )}
            </div>
            <div className="lol-side">
              <Price value={row.a[0]} />
              <Price value={row.a[1]} />
              <Roi value={row.roiA} />
            </div>
            <div className="lol-side">
              <Price value={row.b[0]} />
              <Price value={row.b[1]} />
              <Roi value={row.roiB} />
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

function clock(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function LolPregame() {
  const [start, setStart] = useState("");
  const [days, setDays] = useState(3);
  const [league, setLeague] = useState("");
  const [onlyValue, setOnlyValue] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (params) => {
    setLoading(true);
    setError("");
    try {
      const response = await getLolPregame(params);
      const body = response.data || {};
      if (body.error) setError(body.error);
      setData(body);
      if (!params.start && body.start) setStart(body.start);
    } catch (err) {
      const message = err?.response?.data?.error || "Could not load the LoL board.";
      setError(message);
      setData({ matches: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({ days: 3 });
  }, [load]);

  const matches = data?.matches || [];
  const leagues = useMemo(
    () => [...new Set(matches.map((match) => match.league).filter(Boolean))].sort(),
    [matches]
  );

  const shown = matches.filter((match) => !league || match.league === league);
  const cards = shown.filter((match) => !onlyValue || rowsFor(match).some((row) => (row.roiA || 0) > 0 || (row.roiB || 0) > 0));
  const valueSides = shown.reduce(
    (total, match) => total + rowsFor(match).reduce((n, row) => n + ((row.roiA || 0) > 0) + ((row.roiB || 0) > 0), 0),
    0
  );
  const priced = shown.filter((match) => match.market && (match.market.series || match.market.map1)).length;

  const groups = new Map();
  cards.forEach((match) => {
    const key = match.league || "Other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(match);
  });

  const apply = (next) => {
    const params = { start: next.start ?? start, days: next.days ?? days, fresh: next.fresh ? 1 : undefined };
    if (next.start !== undefined) setStart(next.start);
    if (next.days !== undefined) setDays(next.days);
    load(params);
  };

  return (
    <div className="lol-board">
      <div className="lol-filters">
        <label>from <input type="date" value={start} onChange={(event) => apply({ start: event.target.value })} /></label>
        <label>
          days
          <select value={days} onChange={(event) => apply({ days: Number(event.target.value) })}>
            {[1, 3, 7, 14].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>
          league
          <select value={league} onChange={(event) => setLeague(event.target.value)}>
            <option value="">all</option>
            {leagues.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
        <label>
          show
          <select value={onlyValue ? "value" : "all"} onChange={(event) => setOnlyValue(event.target.value === "value")}>
            <option value="all">every market</option>
            <option value="value">only positive return</option>
          </select>
        </label>
        <button type="button" onClick={() => apply({ fresh: true })} disabled={loading}>Refresh</button>
        <span className="lol-updated">{loading ? "loading…" : data?.fetched_at ? `updated ${clock(data.fetched_at)}` : ""}</span>
      </div>

      {error && <div className="lol-error">{error}</div>}

      <div className="lol-tiles">
        <div><span>Series</span><strong>{shown.length}</strong></div>
        <div><span>Leagues</span><strong>{new Set(shown.map((match) => match.league)).size}</strong></div>
        <div><span>Priced by Polymarket</span><strong>{priced}</strong></div>
        <div><span>Positive-return sides</span><strong>{valueSides}</strong></div>
      </div>

      {[...groups].map(([name, list]) => (
        <section key={name} className="lol-group">
          <div className="lol-shead">
            <h2>{name}</h2>
            <span>{list.length} series</span>
          </div>
          {list.map((match) => (
            <Card key={`${match.match_date}-${match.team_a}-${match.team_b}`} match={match} />
          ))}
        </section>
      ))}

      {!loading && cards.length === 0 && (
        <div className="lol-empty">
          {matches.length ? "Nothing matches this filter." : "No pre-game series in this window."}
        </div>
      )}

      <p className="lol-legend">
        Prices are American odds. <b>Model</b> is the pre-draft model&apos;s fair price; <b>Poly</b> is
        Polymarket&apos;s latest pre-game price with the vig removed. <b>ROI</b> is model probability ÷ market
        probability − 1, shown only where it is positive. It is measured against the snapshot price, not the
        ask you would actually pay, so it overstates small edges. Map 1 assumes the first-listed team starts
        on blue. A card marked <span className="lol-pill lol-pill-warn">as-of backfill</span> was priced after
        the fact, using only games played before it.
      </p>
    </div>
  );
}
